import React, { useState, useEffect, useMemo } from "react";
import { Flex, Text, Select, IconButton, Button, Dialog } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { ActiveIntegrateCard } from "components";
import { PermissionAlertDialog } from "components";
import {
  useGetAtreemoMappings,
  useGetAtreemoResources,
  useSaveAtreemoMappings,
  useDeactivateAtreemo,
  useGetAllBrands,
} from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context";
import { useToast } from "../../../context/ToastContext";

export function ActiveAtreemoIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems, locationsData } = useLocationContext();
  const { setToastMessage } = useToast();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Check if user is owner
  const isOwner = userProfile.user_role === "Owner";

  // separate loading flags for each save
  const [isBrandSaving, setIsBrandSaving] = useState(false);
  const [isLocationSaving, setIsLocationSaving] = useState(false);

  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id);
  const { data: atreemoResourcesData, isLoading: isResourcesLoading } = useGetAtreemoResources(userProfile?.organisation_id);
  const { data: atreemoMappingsData, isLoading: isMappingsLoading } = useGetAtreemoMappings(userProfile?.organisation_id);

  const saveAtreemoMappings = useSaveAtreemoMappings();
  const deactivateAtreemo = useDeactivateAtreemo();

  // Local lists
  const [atreemoBrands, setAtreemoBrands] = useState([]);
  const [atreemoLocations, setAtreemoLocations] = useState([]);
  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);

  // editable state
  // const [brandMappings, setBrandMappings] = useState({});
  const [locationMappings, setLocationMappings] = useState({});

  // originals for diffing
  // const [originalBrandMappings, setOriginalBrandMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});

  // Load Atreemo resources
    useEffect(() => {
      if (atreemoResourcesData) {
        const data = atreemoResourcesData?.data;
      if (!data) return;

      // normalize brands
      const normalizedBrands = (data.available_brands || []).map((b) => ({
        id: String(b.BrandID),
        name: b.BrandName,
      }));

      // normalize locations
      const normalizedLocations = (data.available_locations || []).map((l) => ({
        id: String(l.LocationID),
        name: l.LocationName,
      }));

      setAtreemoBrands(normalizedBrands);
      setAtreemoLocations(normalizedLocations);
    }
  }, [atreemoResourcesData]);

  // Prepare bconnect lists (mirror setup logic)
  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = brandsData.find((b) => b.is_default);
      const nonDefault = brandsData.filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = locationsData.filter((loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null);
      setIndependentLocations(defaultLocations || []);
    }
  }, [brandsData, locationsData]);

  // Build a map of brand -> its locations (prefer embedded brandsData[].locations)
  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;

    bconnectBrands.forEach((b) => {
      // prefer the locations array embedded on the brand object (brandsData)
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        // fallback: try to find locations from the global locationsData if present
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });

    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  // Also provide a flat list of all brand-owned locations
  const allBrandOwnedLocations = useMemo(() => {
    return Object.values(brandLocationsMap).flat();
  }, [brandLocationsMap]);

  // Load existing mappings into local state AND keep originals
  useEffect(() => {
    if (atreemoMappingsData?.data?.mappings) {
      const bm = atreemoMappingsData.data.mappings.brand_mappings || [];
      const lm = atreemoMappingsData.data.mappings.location_mappings || [];

      const brandMap = {};
      const locMap = {};
      const brandOrig = {};
      const locOrig = {};

      bm.forEach((m) => {
        const key = String(m.bconnect_brand_id);
        const entry = {
          atreemo_brand_id: m.atreemo_brand_id || "",
          atreemo_location_id: m.atreemo_location_id || "",
          isMapped: Boolean(m.atreemo_brand_id || m.atreemo_location_id),
        };
        brandMap[key] = entry;
        brandOrig[key] = { atreemo_brand_id: entry.atreemo_brand_id, atreemo_location_id: entry.atreemo_location_id };
      });

      lm.forEach((m) => {
        const key = String(m.bconnect_location_id);
        const entry = {
          atreemo_brand_id: m.atreemo_brand_id || "",
          atreemo_location_id: m.atreemo_location_id || "",
          isMapped: Boolean(m.atreemo_brand_id || m.atreemo_location_id),
        };
        locMap[key] = entry;
        locOrig[key] = { atreemo_brand_id: entry.atreemo_brand_id, atreemo_location_id: entry.atreemo_location_id };
      });

      // setBrandMappings(brandMap);
      setLocationMappings(locMap);
      // setOriginalBrandMappings(brandOrig);
      setOriginalLocationMappings(locOrig);
    }
  }, [atreemoMappingsData]);

  // For the above, here are examples what what ends up being built:
  // Note: The isMapped flag is there as a quick indicator of whether a row is "complete" (has at least one mapping value) or "empty".
  // brandMap = {"2": {atreemo_brand_id: "259", atreemo_location_id: "", isMapped: true}, "3": {atreemo_brand_id: "", atreemo_location_id: "", isMapped: false}}
  // brandOrig = {"2": {atreemo_brand_id: "259", atreemo_location_id: ""}, "3": {atreemo_brand_id: "", atreemo_location_id: ""}}
  // locMap = {"1": {atreemo_brand_id: "259", atreemo_location_id: "1122334455", isMapped: true}, "2": {atreemo_brand_id: "", atreemo_location_id: "", isMapped: false}}
  // locOrig = {"1": {atreemo_brand_id: "259", atreemo_location_id: "1122334455"}, "2": {atreemo_brand_id: "", atreemo_location_id: ""}}
  // brandLocationsMap = {"2": [{id: 1, name: "Bee Cafe - London"}, {id: 2, name: "Bee Cafe - Birmingham"}], "3": [{id: 5, name: "Bee Cafe - Manchester"}]}

  // These are used for both independent locations AND brand-owned locations
  const handleLocationAtreemoBrandChange = (bconnectLocationId, atreemoBrandId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), atreemo_brand_id: atreemoBrandId } }));
  };
  const handleLocationAtreemoLocationChange = (bconnectLocationId, atreemoLocationId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), atreemo_location_id: atreemoLocationId } }));
  };

  // brand-owned locations (the ones grouped under each brand)
  const hasBrandSectionChanges = useMemo(() => {
    const brandLocations = allBrandOwnedLocations || [];

    // 1) If any row in this section is half-filled -> invalid
    for (const loc of brandLocations) {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      const isHalfFilled = (!!currBrand) !== (!!currLoc);
      if (isHalfFilled) return false;
    }

    // 2) Must have at least one fully-populated row that actually changed from original
    const hasAtLeastOneFullyPopulatedChange = brandLocations.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { atreemo_brand_id: "", atreemo_location_id: "" };
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      const origBrand = String(orig.atreemo_brand_id || "");
      const origLoc = String(orig.atreemo_location_id || "");

      // only consider fully populated rows
      if (!currBrand || !currLoc) return false;
      return currBrand !== origBrand || currLoc !== origLoc;
    });

    return Boolean(hasAtLeastOneFullyPopulatedChange);
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  // independent/default locations
  const hasIndependentLocationChanges = useMemo(() => {
    const indep = independentLocations || [];

    // 1) if any row in this section is half-filled -> invalid
    for (const loc of indep) {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      const isHalfFilled = (!!currBrand) !== (!!currLoc);
      if (isHalfFilled) return false;
    }

    // 2) must have at least one fully-populated row that actually changed from original
    const hasAtLeastOneFullyPopulatedChange = indep.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { atreemo_brand_id: "", atreemo_location_id: "" };
      const currBrand = String(curr.atreemo_brand_id || "");
      const currLoc = String(curr.atreemo_location_id || "");
      const origBrand = String(orig.atreemo_brand_id || "");
      const origLoc = String(orig.atreemo_location_id || "");

      if (!currBrand || !currLoc) return false;
      return currBrand !== origBrand || currLoc !== origLoc;
    });

    return Boolean(hasAtLeastOneFullyPopulatedChange);
  }, [independentLocations, locationMappings, originalLocationMappings]);

  const handleSaveLocationMappings = async (section) => {
    try {
      setIsLocationSaving(true);

      // pick locations to include based on section
      let selectedLocations = [];
      if (section === "brands") {
        selectedLocations = allBrandOwnedLocations || [];
      } else if (section === "independent") {
        selectedLocations = independentLocations || [];
      } else {
        selectedLocations = [
          ...(independentLocations || []),
          ...(allBrandOwnedLocations || []),
        ];
      }

      const locationPayload = (selectedLocations || [])
        .map((loc) => {
          const locId = loc.id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currBrand = curr.atreemo_brand_id || null;
          const currLoc = curr.atreemo_location_id || null;
          const orig = originalLocationMappings[key] || {};
          const origBrand = orig.atreemo_brand_id || null;
          const origLoc = orig.atreemo_location_id || null;

          // require both fields to be present to save
          if (!currBrand || !currLoc) return null;

          // skip unchanged rows
          if (currBrand === origBrand && currLoc === origLoc) return null;

          const atreemoBrandObj = atreemoBrands.find((ab) => String(ab.id) === String(currBrand));
          const atreemoLocationObj = atreemoLocations.find((al) => String(al.id) === String(currLoc));
          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            atreemo_brand_id: currBrand,
            atreemo_brand_name: atreemoBrandObj?.name || "",
            atreemo_location_id: currLoc,
            atreemo_location_name: atreemoLocationObj?.name || "",
          };
        })
        .filter(Boolean);

      if (locationPayload.length === 0) {
        setToastMessage("No location changes to save");
        return;
      }

      const payload = {
        organisation_id: userProfile.organisation_id,
        brand_mappings: [], // location-only save
        location_mappings: locationPayload,
      };
      
      const result = await saveAtreemoMappings.mutateAsync(payload);

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      // Update originals for only the saved rows
      const newOrig = { ...originalLocationMappings };
      locationPayload.forEach((p) => {
        newOrig[String(p.bconnect_location_id)] = {
          atreemo_brand_id: p.atreemo_brand_id,
          atreemo_location_id: p.atreemo_location_id,
        };
      });
      setOriginalLocationMappings(newOrig);

      queryClient.invalidateQueries(["atreemo-mappings"]);
      setToastMessage("Location mappings saved successfully");
    } catch (err) {
      console.error("Error saving location mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      setIsLocationSaving(false);
    }
  };

  // Handle disconnect click with permission check
  const handleDisconnectClick = () => {
    if (isOwner) {
      setShowConfirmDialog(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  // Disconnect handler (same as before)
  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);
      const result = await deactivateAtreemo.mutateAsync({ organisation_id: userProfile.organisation_id });
      if (result?.data?.status === "success") {
        queryClient.invalidateQueries(["atreemo-integration-status"]);
      } else {
        throw new Error(result?.data?.message || "Failed to disconnect Atreemo integration");
      }
    } catch (err) {
      console.error("Error disconnecting Atreemo:", err);
      setToastMessage(err?.message || "Failed to disconnect Atreemo");
    } finally {
      setIsDeactivating(false);
      setShowConfirmDialog(false);
    }
  };

  if (!initialItems) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>No locations available.</Text>
      </Flex>
    );
  }

  if (isBrandsLoading || isResourcesLoading || isMappingsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Atreemo integration data...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Atreemo"
        subtitle="Map brands & locations"
        image_url="atreemo.jpg"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        {/* BRANDS section */}
        <ActiveIntegrateCard.Section name="Brands" subtitle="Group: b connect brands with each of their locations">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "32%" }}>b connect location</Text>
              <Text size="2" weight="medium" style={{ width: "32%", paddingLeft: "3px" }}>Atreemo brand</Text>
              <Text size="2" weight="medium" style={{ width: "32%", paddingLeft: "5px" }}>Atreemo location</Text>
            </Flex>

            {(bconnectBrands || []).length > 0 ? (
              (bconnectBrands || []).map((b) => {
                const brandLocations = brandLocationsMap[b.id] || [];
                return (
                  <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                    {/* Brand header */}
                    <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                      <Text size="2" weight="medium" style={{ width: "100%" }}>[{b.name}]</Text>
                    </Flex>

                    {/* Locations under the brand */}
                    {(brandLocations || []).length > 0 ? (
                      brandLocations.map((loc) => {
                        const locId = loc.id;
                        const key = String(locId);
                        const mapping = locationMappings[key] || {};
                        const isMapped = !!mapping.isMapped;
                        return (
                          <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                            <Flex style={{ width: "32%", minWidth: 0 }}>
                              <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                  {loc.name ?? loc.label ?? ""}
                                </Text>
                              </Flex>
                            </Flex>

                            <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={{ width: "32%", minWidth: 0 }}>
                              {isMapped && mapping.atreemo_brand_id ? (
                                <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                  <Text size="2" title={atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                    {atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || ""}
                                  </Text>
                                </Flex>
                              ) : (
                                <Select.Root value={mapping.atreemo_brand_id || ""} onValueChange={(v) => handleLocationAtreemoBrandChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                                  <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.atreemo_brand_id ? (atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || "") : "Select an Atreemo brand"}>
                                      {mapping.atreemo_brand_id ? (atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || "") : "Select an Atreemo brand"}
                                    </div>
                                  </Select.Trigger>
                                  <Select.Content side="bottom" align="start" sideOffset={4}>
                                    {atreemoBrands.map((ab) => <Select.Item key={ab.id} value={ab.id} title={ab.name}>{ab.name}</Select.Item>)}
                                  </Select.Content>
                                </Select.Root>
                              )}
                            </Flex>

                            <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={{ width: "32%", minWidth: 0 }}>
                              {isMapped && mapping.atreemo_location_id ? (
                                <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                  <Text size="2" title={atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                    {atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || ""}
                                  </Text>
                                </Flex>
                              ) : (
                                <Select.Root value={mapping.atreemo_location_id || ""} onValueChange={(v) => handleLocationAtreemoLocationChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                                  <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.atreemo_location_id ? (atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || "") : "Select an Atreemo location"}>
                                      {mapping.atreemo_location_id ? (atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || "") : "Select an Atreemo location"}
                                    </div>
                                  </Select.Trigger>
                                  <Select.Content side="bottom" align="start" sideOffset={4}>
                                    {atreemoLocations.map((al) => <Select.Item key={al.id} value={al.id} title={al.name}>{al.name}</Select.Item>)}
                                  </Select.Content>
                                </Select.Root>
                              )}
                            </Flex>
                          </Flex>
                        );
                      })
                    ) : (
                      <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                        <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                      </Flex>
                    )}
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No brands found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("brands")}
                loading={isLocationSaving}
                disabled={!isOwner || !hasBrandSectionChanges || isLocationSaving}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        {/* LOCATIONS section (independent / default-brand locations) */}
        <ActiveIntegrateCard.Section name="Locations" subtitle="Map independent b connect locations to Atreemo brands/locations">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "32%" }}>b connect location</Text>
              <Text size="2" weight="medium" style={{ width: "32%", paddingLeft: "3px" }}>Atreemo brand</Text>
              <Text size="2" weight="medium" style={{ width: "32%", paddingLeft: "5px" }}>Atreemo location</Text>
            </Flex>

            {(independentLocations || []).length > 0 ? (
              (independentLocations || []).map((loc) => {
                const locId = loc.id ?? loc.value ?? loc.location_id;
                const key = String(locId);
                const mapping = locationMappings[key] || {};
                const isMapped = !!mapping.isMapped;
                return (
                  <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                    <Flex style={{ width: "32%", minWidth: 0 }}>
                      <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                        <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                          {loc.name ?? loc.label ?? ""}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ width: "32%", minWidth: 0 }}>
                      {isMapped && mapping.atreemo_brand_id ? (
                        <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                          <Text size="2" title={atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                            {atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || ""}
                          </Text>
                        </Flex>
                      ) : (
                        <Select.Root value={mapping.atreemo_brand_id || ""} onValueChange={(v) => handleLocationAtreemoBrandChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                          <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                            <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.atreemo_brand_id ? (atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || "") : "Select an Atreemo brand"}>
                              {mapping.atreemo_brand_id ? (atreemoBrands.find((ab) => String(ab.id) === String(mapping.atreemo_brand_id))?.name || "") : "Select an Atreemo brand"}
                            </div>
                          </Select.Trigger>
                          <Select.Content side="bottom" align="start" sideOffset={4}>
                            {atreemoBrands.map((ab) => <Select.Item key={ab.id} value={ab.id} title={ab.name}>{ab.name}</Select.Item>)}
                          </Select.Content>
                        </Select.Root>
                      )}
                    </Flex>

                    <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ width: "32%", minWidth: 0 }}>
                      {isMapped && mapping.atreemo_location_id ? (
                        <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                          <Text size="2" title={atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                            {atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || ""}
                          </Text>
                        </Flex>
                      ) : (
                        <Select.Root value={mapping.atreemo_location_id || ""} onValueChange={(v) => handleLocationAtreemoLocationChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                          <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                            <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.atreemo_location_id ? (atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || "") : "Select an Atreemo location"}>
                              {mapping.atreemo_location_id ? (atreemoLocations.find((al) => String(al.id) === String(mapping.atreemo_location_id))?.name || "") : "Select an Atreemo location"}
                            </div>
                          </Select.Trigger>
                          <Select.Content side="bottom" align="start" sideOffset={4}>
                            {atreemoLocations.map((al) => <Select.Item key={al.id} value={al.id} title={al.name}>{al.name}</Select.Item>)}
                          </Select.Content>
                        </Select.Root>
                      )}
                    </Flex>
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No locations found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("independent")}
                loading={isLocationSaving}
                disabled={!isOwner || !hasIndependentLocationChanges || isLocationSaving}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      {/* Confirm dialog */}
      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Atreemo</Dialog.Title>
          <Dialog.Description size="2" mb="4">Customer data will stop syncing with Atreemo.</Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>Disconnect</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Permission Alert Dialog */}
      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
